import SharedPreferences from "../../../constants/SharedPreferences";
import { getFromStorage } from "../../../util/Utility";

/**
 * Keeps a session's member status from going stale mid-session.
 *
 * MEMBER_STATUS is written into session storage by the login-time lockbox read and by nothing
 * else. An admin approving a member changes it on the server, but the member's own session keeps
 * the value it was given at login - a page reload restores the same storage without re-reading it -
 * so the client goes on acting as though the member were still auto-approved until they log out.
 *
 * The LMS names the caller's current status on every authenticated response. That value is
 * unsigned, so it is treated as a signal and never written to storage: on a difference the lockbox
 * is re-read, which is the path that verifies and persists the real value.
 */

const refreshInFlight = new Map();

/**
 * Set by memberApi at load time. Registered rather than imported because memberApi imports the
 * rest client that calls into here, and a static import back would close that cycle.
 */
let memberLockBoxReader = null;

export function setMemberLockBoxReader(reader) {
  memberLockBoxReader = reader;
}

export const readMemberStatusHeader = (headers) =>
  headers?.["x-member-status"] ?? headers?.["X-Member-Status"] ?? null;

/**
 * Re-reads the lockbox when the server reports a different member status than this session holds.
 *
 * @param {String} sessionId id of the session in question.
 * @param {String} serverMemberStatus status named on the response.
 * @returns {Promise<void>} resolves once any refresh has completed.
 */
export async function refreshMemberStatusIfChanged(sessionId, serverMemberStatus) {
  if (!sessionId || !serverMemberStatus || !memberLockBoxReader) {
    return;
  }

  // A response can arrive after its account logged out, its stored state already cleared, and this
  // read then throws. Nobody awaits this function, so that surfaced as an unhandled rejection —
  // reported as a failed lockbox read during an account switch. There is simply nothing left to
  // compare against, so stop.
  let storedMemberStatus;
  try {
    storedMemberStatus = await getFromStorage(
      sessionId,
      SharedPreferences.MEMBER_STATUS,
    );
  } catch (error) {
    return;
  }

  // No stored value means nothing is relying on a stale one yet; login will write it.
  if (!storedMemberStatus || storedMemberStatus === serverMemberStatus) {
    return;
  }

  const orgMemberId = await getFromStorage(
    sessionId,
    SharedPreferences.ORG_MEMBER_ID,
  );
  if (!orgMemberId) {
    return;
  }

  if (refreshInFlight.has(sessionId)) {
    return refreshInFlight.get(sessionId);
  }

  const refreshPromise = memberLockBoxReader(sessionId, orgMemberId);
  refreshInFlight.set(sessionId, refreshPromise);

  try {
    await refreshPromise;
  } catch (error) {
    // The stale value survives to the next attempt. Failing the request that carried the header
    // would turn an unrelated call into a login error.
    console.log("Could not refresh member status:", error);
  } finally {
    refreshInFlight.delete(sessionId);
  }
}
